import { useMemo } from 'react';
import { REVENUE_SHARE } from '../../config/revenueShare';
import type { BrandKey } from '../../config/brands';
import type { RevenueShareStatementRow } from '../../types/revenueShare';
import { brandKeyOf, groupByBrand } from './brandFilter';

/**
 * Pure composition layer for the Facility Rent Statement Workbench.
 *
 * The server (`revenue_share_statement` RPC) computes the facts: payment-level
 * tuition-first allocation rows for a month. Everything here — rule chips, share
 * overrides, exclusions, manual adjustments, row order — is client arithmetic
 * over those rows. Nothing is persisted; the CSV export is the frozen record.
 */

export interface ManualAdjustment {
  id: string;
  label: string;
  /** Signed dollars, e.g. -250 for a make-good credit. */
  amount: number;
}

export interface WorkbenchState {
  /** Global share fraction (0-1). Prefilled from config; editing = run-time override. */
  sharePct: number;
  /** Rule chip: exclude the BHCS Cash bonus slice from the base (default on). */
  excludeBonus: boolean;
  /** Rule chip: keep fees & materials out of the statement (default on). */
  excludeFeesMaterials: boolean;
  /** program_ids in display order; programs not listed keep default (base desc) order. */
  rowOrder: string[];
  /** Per-program share overrides (fraction 0-1), keyed by program_id. */
  shareOverrides: Record<string, number>;
  /** Explicitly excluded program_ids (moved to the restorable Excluded group). */
  excluded: string[];
  /**
   * Explicitly restored program_ids. Zero-rent-base programs (e.g. fee-only,
   * no tuition) are excluded by default; restoring one records it here.
   */
  included: string[];
  adjustments: ManualAdjustment[];
}

export function defaultWorkbenchState(): WorkbenchState {
  return {
    sharePct: REVENUE_SHARE.defaultPct,
    excludeBonus: true,
    excludeFeesMaterials: true,
    rowOrder: [],
    shareOverrides: {},
    excluded: [],
    included: [],
    adjustments: [],
  };
}

export interface ProgramLine {
  programId: string;
  programName: string;
  nPayments: number;
  nRefunds: number;
  grossTuitionCents: number;
  bonusCents: number;
  feesMaterialsCents: number;
  /** tuition − bonus (the core rules; chip add-backs are separate lines). */
  baseCents: number;
  /** Effective share for this row (override ?? global). */
  sharePct: number;
  overridden: boolean;
  rentCents: number;
  /** Payment-level audit trail, ordered by date. */
  drilldown: RevenueShareStatementRow[];
}

export interface AddBackLine {
  key: 'bonus' | 'feesMaterials';
  label: string;
  amountCents: number;
  rentCents: number;
}

export interface AdjustmentLine extends ManualAdjustment {
  amountCents: number;
  rentCents: number;
}

export interface StatementTotals {
  baseCents: number;
  rentCents: number;
  grossTuitionCents: number;
  bonusExcludedCents: number;
  feesMaterialsCents: number;
  /** Everything collected this month across included programs (tuition + fees + materials). */
  collectionsCents: number;
  nPayments: number;
  nRefunds: number;
}

export interface StatementModel {
  programs: ProgramLine[];
  excludedPrograms: ProgramLine[];
  addBacks: AddBackLine[];
  adjustments: AdjustmentLine[];
  totals: StatementTotals;
}

const rentFor = (baseCents: number, pct: number): number => Math.round(baseCents * pct);

export function buildStatementModel(
  rows: RevenueShareStatementRow[],
  state: WorkbenchState,
): StatementModel {
  const byProgram = new Map<string, ProgramLine>();
  for (const r of rows) {
    let line = byProgram.get(r.program_id);
    if (!line) {
      line = {
        programId: r.program_id,
        programName: r.program_name,
        nPayments: 0,
        nRefunds: 0,
        grossTuitionCents: 0,
        bonusCents: 0,
        feesMaterialsCents: 0,
        baseCents: 0,
        sharePct: state.sharePct,
        overridden: false,
        rentCents: 0,
        drilldown: [],
      };
      byProgram.set(r.program_id, line);
    }
    line.grossTuitionCents += r.tuition_cents;
    line.bonusCents += r.bonus_excluded_cents;
    line.feesMaterialsCents += r.fees_cents + r.materials_cents;
    if (r.kind === 'refund') line.nRefunds += 1;
    else line.nPayments += 1;
    line.drilldown.push(r);
  }

  const all = [...byProgram.values()];
  for (const line of all) {
    line.baseCents = line.grossTuitionCents - line.bonusCents;
    const override = state.shareOverrides[line.programId];
    line.overridden = override !== undefined;
    line.sharePct = override ?? state.sharePct;
    line.rentCents = rentFor(line.baseCents, line.sharePct);
    line.drilldown.sort(
      (a, b) => a.payment_date.localeCompare(b.payment_date) || a.payment_id.localeCompare(b.payment_id),
    );
  }

  // Default order: base desc, then name. rowOrder pins listed programs first, in order.
  all.sort((a, b) => b.baseCents - a.baseCents || a.programName.localeCompare(b.programName));
  const pinned = state.rowOrder.filter((id) => byProgram.has(id));
  const rank = new Map(pinned.map((id, i) => [id, i]));
  all.sort((a, b) => {
    const ra = rank.get(a.programId);
    const rb = rank.get(b.programId);
    if (ra !== undefined && rb !== undefined) return ra - rb;
    if (ra !== undefined) return -1;
    if (rb !== undefined) return 1;
    return 0; // stable: keeps the base-desc order among unpinned rows
  });

  // Excluded when explicitly excluded, or when the rent base is exactly $0
  // (fee-only programs — nothing to share) unless explicitly restored.
  // Negative bases (refund months) stay in: dropping them would overstate rent.
  const excludedSet = new Set(state.excluded);
  const includedSet = new Set(state.included);
  const isExcluded = (l: ProgramLine) =>
    excludedSet.has(l.programId) || (l.baseCents === 0 && !includedSet.has(l.programId));
  const programs = all.filter((l) => !isExcluded(l));
  const excludedPrograms = all.filter(isExcluded);

  const sum = (f: (l: ProgramLine) => number) => programs.reduce((s, l) => s + f(l), 0);
  const bonusTotal = sum((l) => l.bonusCents);
  const feesTotal = sum((l) => l.feesMaterialsCents);

  // Turning a rule chip off inserts an explicit add-back line (at the global share)
  // instead of silently rewriting program rows — the statement stays self-explanatory.
  const addBacks: AddBackLine[] = [];
  if (!state.excludeBonus && bonusTotal !== 0) {
    addBacks.push({
      key: 'bonus',
      label: 'BHCS Cash bonus (rule off — added back)',
      amountCents: bonusTotal,
      rentCents: rentFor(bonusTotal, state.sharePct),
    });
  }
  if (!state.excludeFeesMaterials && feesTotal !== 0) {
    addBacks.push({
      key: 'feesMaterials',
      label: 'Fees & materials (rule off — added back)',
      amountCents: feesTotal,
      rentCents: rentFor(feesTotal, state.sharePct),
    });
  }

  const adjustments: AdjustmentLine[] = state.adjustments.map((a) => {
    const amountCents = Math.round(a.amount * 100);
    return { ...a, amountCents, rentCents: rentFor(amountCents, state.sharePct) };
  });

  const extraBase =
    addBacks.reduce((s, l) => s + l.amountCents, 0) + adjustments.reduce((s, l) => s + l.amountCents, 0);
  const extraRent =
    addBacks.reduce((s, l) => s + l.rentCents, 0) + adjustments.reduce((s, l) => s + l.rentCents, 0);

  const totals: StatementTotals = {
    baseCents: sum((l) => l.baseCents) + extraBase,
    rentCents: sum((l) => l.rentCents) + extraRent,
    grossTuitionCents: sum((l) => l.grossTuitionCents),
    bonusExcludedCents: state.excludeBonus ? bonusTotal : 0,
    feesMaterialsCents: feesTotal,
    collectionsCents: sum((l) => l.grossTuitionCents + l.feesMaterialsCents),
    nPayments: sum((l) => l.nPayments),
    nRefunds: sum((l) => l.nRefunds),
  };

  return { programs, excludedPrograms, addBacks, adjustments, totals };
}

/** The statement rows one brand's statement is built from. Rows without a brand read as the default. */
export function rowsForBrand(rows: RevenueShareStatementRow[], brand: BrandKey): RevenueShareStatementRow[] {
  return rows.filter((r) => brandKeyOf(r.brand_key) === brand);
}

/** What another brand collected in the month, for the "not in this statement" note. */
export interface OtherBrandCollections {
  brand: BrandKey;
  nPayments: number;
  nRefunds: number;
  /** Tuition + fees + materials, net of refunds, before any statement rule. */
  netCents: number;
}

/**
 * Every brand other than `brand` that has rows in the month, with what it collected.
 *
 * Raw sums, not a composed statement: this answers "how much money is this statement leaving
 * out", which must not depend on the rules or exclusions chosen for the statement on screen.
 */
export function otherBrandCollections(rows: RevenueShareStatementRow[], brand: BrandKey): OtherBrandCollections[] {
  return groupByBrand(rows, (r) => r.brand_key)
    .filter((g) => g.brand !== brand)
    .map((g) => ({
      brand: g.brand,
      nPayments: g.rows.filter((r) => r.kind !== 'refund').length,
      nRefunds: g.rows.filter((r) => r.kind === 'refund').length,
      netCents: g.rows.reduce((s, r) => s + r.tuition_cents + r.fees_cents + r.materials_cents, 0),
    }));
}

export function useStatementModel(
  rows: RevenueShareStatementRow[],
  state: WorkbenchState,
): StatementModel {
  return useMemo(() => buildStatementModel(rows, state), [rows, state]);
}

// ─── URL state (Copy link / refresh = recompute from facts + URL) ────────────

/** Serialize only the fields that differ from the defaults, compactly. */
export function serializeWorkbenchState(state: WorkbenchState): string {
  const d = defaultWorkbenchState();
  const out: Record<string, unknown> = {};
  if (state.sharePct !== d.sharePct) out.p = state.sharePct;
  if (state.excludeBonus !== d.excludeBonus) out.b = state.excludeBonus ? 1 : 0;
  if (state.excludeFeesMaterials !== d.excludeFeesMaterials) out.f = state.excludeFeesMaterials ? 1 : 0;
  if (state.rowOrder.length > 0) out.o = state.rowOrder;
  if (Object.keys(state.shareOverrides).length > 0) out.v = state.shareOverrides;
  if (state.excluded.length > 0) out.x = state.excluded;
  if (state.included.length > 0) out.i = state.included;
  if (state.adjustments.length > 0) out.a = state.adjustments.map((a) => [a.id, a.label, a.amount]);
  if (Object.keys(out).length === 0) return '';
  return btoa(encodeURIComponent(JSON.stringify(out)));
}

export function parseWorkbenchState(raw: string | null): WorkbenchState {
  const state = defaultWorkbenchState();
  if (!raw) return state;
  try {
    const obj: unknown = JSON.parse(decodeURIComponent(atob(raw)));
    if (typeof obj !== 'object' || obj === null) return state;
    const o = obj as Record<string, unknown>;
    if (typeof o.p === 'number' && o.p >= 0 && o.p <= 1) state.sharePct = o.p;
    if (o.b !== undefined) state.excludeBonus = o.b === 1;
    if (o.f !== undefined) state.excludeFeesMaterials = o.f === 1;
    if (Array.isArray(o.o)) state.rowOrder = o.o.filter((x): x is string => typeof x === 'string');
    if (typeof o.v === 'object' && o.v !== null) {
      for (const [k, v] of Object.entries(o.v as Record<string, unknown>)) {
        if (typeof v === 'number' && v >= 0 && v <= 1) state.shareOverrides[k] = v;
      }
    }
    if (Array.isArray(o.x)) state.excluded = o.x.filter((x): x is string => typeof x === 'string');
    if (Array.isArray(o.i)) state.included = o.i.filter((x): x is string => typeof x === 'string');
    if (Array.isArray(o.a)) {
      state.adjustments = o.a.flatMap((t): ManualAdjustment[] => {
        if (!Array.isArray(t) || t.length !== 3) return [];
        const [id, label, amount] = t as unknown[];
        if (typeof id !== 'string' || typeof label !== 'string' || typeof amount !== 'number') return [];
        return [{ id, label, amount }];
      });
    }
    return state;
  } catch {
    return state;
  }
}

// ─── Month helpers (pure YYYY-MM strings — no Date/timezone involvement) ─────

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return `${MONTH_NAMES[(m ?? 1) - 1]} ${y}`;
}

export function stepMonth(month: string, delta: 1 | -1): string {
  const [y, m] = month.split('-').map(Number);
  const idx = y * 12 + (m - 1) + delta;
  const ny = Math.floor(idx / 12);
  const nm = (idx % 12) + 1;
  return `${ny}-${String(nm).padStart(2, '0')}`;
}

export const centsToDollars = (cents: number): string =>
  (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ─── Exclude / restore (maintain both explicit lists together) ───────────────

/** State patch that excludes a program (also clears any explicit restore). */
export function excludeProgramPatch(state: WorkbenchState, programId: string): Partial<WorkbenchState> {
  return {
    excluded: [...state.excluded.filter((id) => id !== programId), programId],
    included: state.included.filter((id) => id !== programId),
  };
}

/** State patch that restores a program (also marks auto-excluded $0 rows as kept). */
export function restoreProgramPatch(state: WorkbenchState, programId: string): Partial<WorkbenchState> {
  return {
    excluded: state.excluded.filter((id) => id !== programId),
    included: [...state.included.filter((id) => id !== programId), programId],
  };
}
